import * as yup from 'yup'
import { useTranslation } from 'react-i18next'
import { useMemo } from 'react'

import { shouldRunEnrolmentCheck } from '../enrolmentCheck'

import { isValidWebsiteURL } from '@percent/utility'
import { useEducationHostedForm } from '@percent/education-hosted-form/hooks/useEducationHostedForm/useEducationHostedForm'

const MAX_FIELD_LENGTH = 255

// The add-organisation flow is identified by organisationId being empty —
// picking an existing org sets it to a real UUID; clicking the add-organisation
// action callback navigates without touching it. So `!id` ⇒ user is in the
// add-organisation flow.
const isAddOrganisationFlow = (id: string) => !id

export const useValidationSchema = () => {
  const { t } = useTranslation()
  const { partnerConfig } = useEducationHostedForm()
  const enrolmentCheckEnabled = shouldRunEnrolmentCheck(partnerConfig?.configuration)

  const schema = useMemo(
    () =>
      yup.object().shape({
        country: yup.string().required(t('validation.required')),
        organisationId: yup.string().required(t('validation.required')),
        firstName: yup.string().required(t('validation.required')).max(MAX_FIELD_LENGTH, t('validation.maxLength')),
        lastName: yup.string().required(t('validation.required')).max(MAX_FIELD_LENGTH, t('validation.maxLength')),
        email: yup
          .string()
          .required(t('validation.required'))
          .email(t('validation.email'))
          .max(MAX_FIELD_LENGTH, t('validation.maxLength')),
        dateOfBirth: enrolmentCheckEnabled
          ? yup.date().nullable().required(t('validation.required'))
          : yup.date().nullable(),
        organisationName: yup
          .string()
          .when('organisationId', {
            is: isAddOrganisationFlow,
            then: yup.string().required(t('validation.required'))
          })
          .max(MAX_FIELD_LENGTH, t('validation.maxLength')),
        organisationWebsite: isValidWebsiteURL()
          .trim()
          .when('organisationId', {
            is: isAddOrganisationFlow,
            then: isValidWebsiteURL().trim().required(t('validation.required'))
          })
          .max(MAX_FIELD_LENGTH, t('validation.maxLength')),
        organisationAddressLine1: yup
          .string()
          .when('organisationId', {
            is: isAddOrganisationFlow,
            then: yup.string().required(t('validation.required'))
          })
          .max(MAX_FIELD_LENGTH, t('validation.maxLength')),
        organisationAddressLine2: yup.string().max(MAX_FIELD_LENGTH, t('validation.maxLength')),
        organisationCity: yup
          .string()
          .when('organisationId', {
            is: isAddOrganisationFlow,
            then: yup.string().required(t('validation.required'))
          })
          .max(MAX_FIELD_LENGTH, t('validation.maxLength')),
        organisationState: yup
          .string()
          .when('organisationId', {
            is: isAddOrganisationFlow,
            then: yup.string().required(t('validation.required'))
          })
          .max(MAX_FIELD_LENGTH, t('validation.maxLength')),
        organisationPostalCode: yup
          .string()
          .when('organisationId', {
            is: isAddOrganisationFlow,
            then: yup.string().required(t('validation.required'))
          })
          .max(MAX_FIELD_LENGTH, t('validation.maxLength'))
      }),
    [t, enrolmentCheckEnabled]
  )

  return { schema }
}
